import { bffApi } from "../../autogen/bff-api";
import type { AppDispatch } from "../redux/store";
import { base64Encode } from "../utils/base64";
import { withAgentToolHeader } from "./agentToolHeader";
import { buildContractReportQuery } from "./contractReportQuery";

export type ToolResult =
  | { ok: true; data: unknown }
  | { ok: false; deviationCode: string; message: string };

export type ToolContext = {
  dispatch: AppDispatch;
  auth: { organizationId: string; personId: string };
};

export const SEARCH_TOOL_NAME = "search_tender_documents";
export const DRAFT_TOOL_NAME = "draft_content";

// Surface the real failure to the planner so it can self-correct — including the raw body of a
// PARSING_ERROR (a non-JSON 500 body, e.g. a server validation message).
const toError = (err: unknown): ToolResult => {
  const e = err as { status?: number | string; originalStatus?: number; data?: unknown; error?: string };
  const status = String(e?.originalStatus ?? e?.status ?? "UNKNOWN");
  const data = e?.data;
  if (data && typeof data === "object") {
    const d = data as { errorCode?: string; message?: string };
    return { ok: false, deviationCode: d.errorCode ?? status, message: d.message ?? d.errorCode ?? JSON.stringify(data) };
  }
  const raw = typeof data === "string" && data.trim() ? data.trim() : e?.error;
  return { ok: false, deviationCode: status, message: raw ? `${status}: ${raw}` : "Forespørselen feilet." };
};

type EndpointInitiator = { initiate: (arg: Record<string, unknown>) => unknown };

// Per-endpoint argument adapters for the rare endpoints the OpenAPI spec can't describe.
// listContractReports is the one endpoint whose `q` is a base64-encoded JSON query that must
// carry the org id — the model can't produce that, so the executor builds it from the real filter
// params the catalog advertises (see contractReportQuery.ts).
const ARG_ADAPTERS: Record<string, (args: Record<string, unknown>, ctx: ToolContext) => Record<string, unknown>> = {
  listContractReports: (args, ctx) => ({
    q: base64Encode(JSON.stringify(buildContractReportQuery(args, ctx.auth.organizationId))),
  }),
};

// Generic execution: any catalog tool is just an operationId. Auth/org/person context is injected
// here; the model only supplies the domain params advertised by the catalog. The rare endpoint
// that needs special argument shaping goes through ARG_ADAPTERS above.
export const runEndpointTool = async (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolResult> => {
  const endpoints = bffApi.endpoints as unknown as Record<string, EndpointInitiator>;
  const endpoint = endpoints[operationId];
  if (!endpoint) {
    return { ok: false, deviationCode: "UNKNOWN_TOOL", message: `Ukjent verktøy: ${operationId}` };
  }
  const authContext = {
    organizationId: ctx.auth.organizationId,
    personId: ctx.auth.personId,
    orgId: ctx.auth.organizationId,
  };
  const finalArgs = ARG_ADAPTERS[operationId] ? ARG_ADAPTERS[operationId](args, ctx) : args;
  try {
    const data = await withAgentToolHeader(operationId, () => {
      const thunk = endpoint.initiate({ ...authContext, ...finalArgs });
      const dispatched = ctx.dispatch(thunk as never) as unknown as { unwrap: () => Promise<unknown> };
      return dispatched.unwrap();
    });
    return { ok: true, data };
  } catch (err) {
    return toError(err);
  }
};

export const runTool = async (
  toolName: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
  searchDocuments: (query: string) => Promise<ToolResult>,
  generateDraft: (args: Record<string, unknown>) => Promise<ToolResult>,
): Promise<ToolResult> => {
  if (toolName === SEARCH_TOOL_NAME) {
    const query = String((args as { query?: unknown })?.query ?? "");
    return searchDocuments(query);
  }
  if (toolName === DRAFT_TOOL_NAME) {
    return generateDraft(args);
  }
  return runEndpointTool(toolName, args, ctx);
};

const PLANNER_RESULT_MAX_CHARS = 6000;

// The planner only needs to know what a tool returned (and any ids for chaining), not the full
// payload. Bound it generically so the planning model stays fast; the answer phase always
// receives the full result. The budget fits a typical list so the planner can scan it without
// re-listing; on the rare overflow we tell it NOT to re-call (re-calling just truncates again).
export const boundForPlanner = (result: ToolResult): string => {
  const json = result.ok ? JSON.stringify(result.data) : JSON.stringify(result);
  if (json.length <= PLANNER_RESULT_MAX_CHARS) {
    return json;
  }
  return `${json.slice(0, PLANNER_RESULT_MAX_CHARS)}… [forkortet — de første resultatene vises; ikke kall verktøyet på nytt for dette]`;
};
